import React, { useState } from 'react';
import { Box, Paper, Typography, Divider, Tabs, Tab, styled, Button, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemIcon, ListItemText, Tooltip, IconButton, Grid, useTheme, useMediaQuery, FormControl, Select, MenuItem } from '@mui/material';
// PersonelListesi artık kullanılmıyor - Vardiya Listesi içinden erişilebilir
import VardiyaListesi from './VardiyaListesi';
import GirisCikisRaporlari from './GirisCikisRaporlari';
import ToplamSure from './ToplamSure';
import { Download as DownloadIcon, QrCode as QrCodeIcon, Info as InfoIcon, Schedule as ScheduleIcon, AssignmentTurnedIn as ReportIcon, Timer as TimerIcon, Palette as PaletteIcon, AccessTime as AccessTimeIcon } from '@mui/icons-material';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { useAuth } from '../../contexts/AuthContext';
import VardiyaQrPrintModal from './VardiyaQrPrintModal';
import VardiyaQrIndirModal from './VardiyaQrIndirModal';
import { useTranslation } from 'react-i18next';

// Başlık alanı için styled component
const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
}));

// Kaydırılabilir ana içerik için styled component - Mobilde scroll yok
const ScrollableContent = styled(Box)(({ theme }) => ({
  // Desktop'ta sabit yükseklik ve scroll
  [theme.breakpoints.up('md')]: {
    height: 'calc(100vh - 150px)',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f1f1',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#888',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#555',
    },
  },
  // Mobilde scroll yok, içerik akıyor
  [theme.breakpoints.down('md')]: {
    height: 'auto',
    overflowY: 'visible',
  },
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vardiya-tabpanel-${index}`}
      aria-labelledby={`vardiya-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 1 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `vardiya-tab-${index}`,
    'aria-controls': `vardiya-tabpanel-${index}`,
  };
}

interface ShiftsProps {
  branchId?: string;
  isManager?: boolean;
}

const Shifts: React.FC<ShiftsProps> = ({ branchId, isManager = false }) => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');
  const tr = (trText: string, enText: string) => (isEnglish ? enText : trText);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [value, setValue] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const { userDetails } = useAuth();
  const [qrDesignModalOpen, setQrDesignModalOpen] = useState(false);
  const [qrDownloadModalOpen, setQrDownloadModalOpen] = useState(false);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleOpenInfoModal = () => {
    setInfoModalOpen(true);
  };

  const handleCloseInfoModal = () => {
    setInfoModalOpen(false);
  };

  // Geçerli sekmeye göre başlık belirleme
  const getTabTitle = () => {
    switch (value) {
      case 0:
        return tr("Vardiya Listesi", "Shift List");
      case 1:
        return tr("Giriş Çıkış Raporları", "Check-In / Check-Out Reports");
      case 2:
        return tr("Toplam Süre", "Total Duration");
      default:
        return tr("Vardiya Yönetimi", "Shift Management");
    }
  };

  // Excel'e veri aktarma işlevi
  const exportToExcel = async () => {
    try {
      setExporting(true);
      
      // Excel workbook oluştur
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(getTabTitle());
      
      // Aktif sekmeye göre farklı sütun başlıkları ve veriler hazırla
      // Tab 0: Vardiya Listesi
      // Tab 1: Giriş Çıkış Raporları
      // Tab 2: Toplam Süre
      if (value === 0) { // Vardiya Listesi
        worksheet.columns = [
          { header: tr('Vardiya Adı', 'Shift Name'), key: 'name', width: 30 },
          { header: tr('Giriş Saati', 'Start Time'), key: 'startTime', width: 15 },
          { header: tr('Çıkış Saati', 'End Time'), key: 'endTime', width: 15 },
          { header: tr('Personel Sayısı', 'Personnel Count'), key: 'personnelCount', width: 15 },
          { header: tr('Geç Kalma Toleransı', 'Late Tolerance'), key: 'lateTolerance', width: 20 },
          { header: tr('Erken Çıkma Toleransı', 'Early Exit Tolerance'), key: 'earlyExitTolerance', width: 20 }
        ];
        
        // Özel olarak veri ekleme işlemi için event oluşturup yayınlanacak
        const event = new CustomEvent('export-vardiya-to-excel', { 
          detail: { 
            worksheet,
            branchId,
            isManager
          } 
        });
        window.dispatchEvent(event);
      }
      else if (value === 1) { // Giriş Çıkış Raporları
        worksheet.columns = [
          { header: tr('Personel', 'Personnel'), key: 'personnel', width: 30 },
          { header: tr('Vardiya', 'Shift'), key: 'shift', width: 20 },
          { header: tr('Tarih', 'Date'), key: 'date', width: 15 },
          { header: tr('Giriş Saati', 'Start Time'), key: 'checkIn', width: 15 },
          { header: tr('Çıkış Saati', 'End Time'), key: 'checkOut', width: 15 },
          { header: tr('Toplam Süre', 'Total Duration'), key: 'totalHours', width: 15 },
          { header: tr('Durum', 'Status'), key: 'status', width: 15 }
        ];
        
        // Özel olarak veri ekleme işlemi için event oluşturup yayınlanacak
        const event = new CustomEvent('export-report-to-excel', { 
          detail: { 
            worksheet,
            branchId,
            isManager
          } 
        });
        window.dispatchEvent(event);
      }
      else if (value === 2) { // Toplam Süre
        worksheet.columns = [
          { header: tr('Personel', 'Personnel'), key: 'personnel', width: 30 },
          { header: tr('Dönem', 'Period'), key: 'period', width: 20 },
          { header: tr('Çalışma Saati', 'Working Time'), key: 'workHours', width: 15 },
          { header: tr('Ek Çalışma', 'Extra Work'), key: 'extraWork', width: 15 },
          { header: tr('Toplam Süre', 'Total Duration'), key: 'totalHours', width: 15 }
        ];
        
        // Özel olarak veri ekleme işlemi için event oluşturup yayınlanacak
        const event = new CustomEvent('export-toplamsure-to-excel', { 
          detail: { 
            worksheet,
            branchId,
            isManager
          } 
        });
        window.dispatchEvent(event);
      }
      
      // Stil tanımlamaları
      const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FF4F81BD' } },
        border: {
          top: { style: 'thin' as const },
          left: { style: 'thin' as const },
          bottom: { style: 'thin' as const },
          right: { style: 'thin' as const }
        }
      };
      
      // Başlık stilini uygula
      worksheet.getRow(1).eachCell(cell => {
        cell.style = headerStyle;
      });
      
      // Zebrastripe (alternatif satır renklendirme)
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) { // başlığı atla
          const fillColor = rowNumber % 2 === 0 ? 'FFF2F2F2' : 'FFFFFFFF';
          row.eachCell(cell => {
            cell.style = {
              fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: fillColor } },
              border: {
                top: { style: 'thin' as const },
                left: { style: 'thin' as const },
                bottom: { style: 'thin' as const },
                right: { style: 'thin' as const }
              }
            };
          });
        }
      });
      
      // Excel dosyasını oluştur ve indir
      setTimeout(async () => {
        const buffer = await workbook.xlsx.writeBuffer();
        const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        saveAs(new Blob([buffer]), `${getTabTitle()}_${currentDate}.xlsx`);
        setExporting(false);
      }, 500); // İlgili komponentın veri eklemesine zaman tanımak için küçük bir gecikme
      
    } catch (error) {
      console.error('Excel dışa aktarma hatası:', error);
      alert(tr('Excel dosyası oluşturulurken bir hata oluştu.', 'An error occurred while creating the Excel file.'));
      setExporting(false);
    }
  };

  // QR kod indirme işlevi (Tek QR / single — yönsüz; companyId gömülü)
  return (
    <Paper sx={{ 
      p: { xs: 1, md: 2 }, 
      mt: { xs: 1, md: 2 }, 
      height: { xs: 'auto', md: 'calc(100vh - 120px)' }, 
      minHeight: { xs: 'auto', md: 'calc(100vh - 120px)' },
      display: 'flex', 
      flexDirection: 'column' 
    }}>
      <HeaderContainer 
        sx={{ 
          flexDirection: { xs: 'column', md: 'row' }, 
          alignItems: { xs: 'flex-start', md: 'center' }, 
          gap: { xs: 1.5, md: 0 } 
        }}
      >
        {/* Başlık */}
        <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
          <AccessTimeIcon sx={{ fontSize: { xs: 24, md: 32 }, mr: 1.5, color: 'primary.main' }} />
          <Typography variant={isMobile ? "h6" : "h5"} component="h1" fontWeight="bold">
            {isManager ? tr('Şube ', 'Branch ') : ''}{getTabTitle()}
          </Typography>
          {/* Mobile: Sadece info butonu başlık yanında */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, ml: 'auto' }}>
            <Tooltip title={tr('Yardım ve Açıklamalar', 'Help and Details')}>
              <IconButton color="primary" onClick={handleOpenInfoModal} size="small">
                <InfoIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Desktop: Butonlar */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
          <Tooltip title={tr('Yardım ve Açıklamalar', 'Help and Details')}>
            <IconButton 
              color="primary" 
              onClick={handleOpenInfoModal}
              sx={{ mr: 0.5 }}
            >
              <InfoIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            color="success"
            startIcon={<QrCodeIcon />}
            onClick={() => setQrDownloadModalOpen(true)}
            disabled={!userDetails?.companyId && !branchId}
            size="small"
          >
            {isManager ? tr('Şube QR', 'Branch QR') : tr('QR İndir', 'Download QR')}
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<PaletteIcon />}
            onClick={() => setQrDesignModalOpen(true)}
            size="small"
          >
            {tr('QR Tasarla', 'Design QR')}
          </Button>
          <Button
            variant="contained"
            color="info"
            startIcon={<DownloadIcon />}
            onClick={exportToExcel}
            disabled={exporting}
            size="small"
          >
            {exporting ? tr('İndiriliyor...', 'Downloading...') : tr('Excel İndir', 'Download Excel')}
          </Button>
        </Box>

        {/* Mobile: Grid Butonlar */}
        <Grid 
          container 
          spacing={1} 
          sx={{ 
            display: { xs: 'flex', md: 'none' }, 
            width: '100%' 
          }}
        >
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="contained"
              color="success"
              startIcon={<QrCodeIcon />}
              onClick={() => setQrDownloadModalOpen(true)}
              disabled={!userDetails?.companyId && !branchId}
              size="small"
            >
              {isManager ? tr('Şube QR', 'Branch QR') : tr('QR İndir', 'Download QR')}
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="contained"
              color="warning"
              startIcon={<PaletteIcon />}
              onClick={() => setQrDesignModalOpen(true)}
              size="small"
            >
              {tr('QR Tasarla', 'Design QR')}
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="contained"
              color="info"
              startIcon={<DownloadIcon />}
              onClick={exportToExcel}
              disabled={exporting}
              size="small"
            >
              {exporting ? tr('İndiriliyor...', 'Downloading...') : tr('Excel İndir', 'Download Excel')}
            </Button>
          </Grid>
        </Grid>
      </HeaderContainer>
      <Divider sx={{ mb: 1 }} />
      
      <Box sx={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column', overflow: { xs: 'visible', md: 'hidden' } }}>
        {/* Desktop: Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: { xs: 'none', md: 'block' } }}>
          <Tabs 
            value={value} 
            onChange={handleChange} 
            aria-label={tr('vardiya yönetim tabları', 'shift management tabs')}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ minHeight: '40px' }}
          >
            <Tab label={tr('Vardiya Listesi', 'Shift List')} {...a11yProps(0)} sx={{ py: 0.5 }} />
            <Tab label={tr('Giriş Çıkış Raporları', 'Check-In / Check-Out Reports')} {...a11yProps(1)} sx={{ py: 0.5 }} />
            <Tab label={tr('Toplam Süre', 'Total Duration')} {...a11yProps(2)} sx={{ py: 0.5 }} />
          </Tabs>
        </Box>

        {/* Mobile: Dropdown Menü */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 1 }}>
          <FormControl fullWidth size="small">
            <Select
              id="vardiya-tab-select"
              value={value}
              onChange={(e) => setValue(e.target.value as number)}
              displayEmpty
              sx={{
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 1
                }
              }}
            >
              <MenuItem value={0}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScheduleIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography>{tr('Vardiya Listesi', 'Shift List')}</Typography>
                </Box>
              </MenuItem>
              <MenuItem value={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ReportIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography>{tr('Giriş Çıkış Raporları', 'Check-In / Check-Out Reports')}</Typography>
                </Box>
              </MenuItem>
              <MenuItem value={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TimerIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography>{tr('Toplam Süre', 'Total Duration')}</Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
        <ScrollableContent>
          <TabPanel value={value} index={0}>
            <VardiyaListesi branchId={branchId} isManager={isManager} />
          </TabPanel>
          <TabPanel value={value} index={1}>
            <GirisCikisRaporlari branchId={branchId} isManager={isManager} />
          </TabPanel>
          <TabPanel value={value} index={2}>
            <ToplamSure branchId={branchId} isManager={isManager} />
          </TabPanel>
        </ScrollableContent>
      </Box>

      {/* Bilgi Modalı */}
      <Dialog
        open={infoModalOpen}
        onClose={handleCloseInfoModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
          {isManager ? tr('Şube Vardiya Yönetimi', 'Branch Shift Management') : tr('Vardiya Yönetimi', 'Shift Management')} {tr('Hakkında Bilgiler', 'Information')}
        </DialogTitle>
        <DialogContent dividers>
          <Typography paragraph>
            {tr(
              `${isManager ? 'Şube Vardiya Yönetimi' : 'Vardiya Yönetimi'} modülü, ${isManager ? 'şubenizin' : 'şirketinizin'} personel vardiyalarını etkin bir şekilde yönetmenizi sağlayan bir dizi araç içerir. Aşağıda her bir sekmede gerçekleştirebileceğiniz işlemler detaylı olarak açıklanmıştır:`,
              `${isManager ? 'Branch Shift Management' : 'Shift Management'} includes tools to manage ${isManager ? 'your branch' : 'your company'} personnel shifts effectively. The actions available in each tab are explained below:`
            )}
          </Typography>
          
          <List>
            <ListItem>
              <ListItemIcon>
                <ScheduleIcon sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary={tr('Vardiya Listesi', 'Shift List')}
                secondary={tr(
                  `Bu sekmede ${isManager ? 'şubenize' : 'şirketinize'} ait vardiyaları oluşturabilir, düzenleyebilir ve yönetebilirsiniz. Her vardiya için özel giriş-çıkış saatleri, gecikme toleransları ve izleme ayarları tanımlayabilirsiniz. Vardiyalara personel atama, kaldırma ve değiştirme işlemleri de bu sekme üzerinden yapılabilir. Vardiya detaylarını ve her vardiyada çalışan personel sayısını görüntüleyebilirsiniz.`,
                  `In this tab, you can create, edit, and manage shifts for ${isManager ? 'your branch' : 'your company'}. You can define custom check-in/check-out times, delay tolerances, and monitoring settings, and also assign, remove, or change personnel for each shift.`
                )}
              />
            </ListItem>
            
            <Divider variant="inset" component="li" />
            
            <ListItem>
              <ListItemIcon>
                <ReportIcon sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary={tr('Giriş Çıkış Raporları', 'Check-In / Check-Out Reports')}
                secondary={tr(
                  'Bu sekmede personelin vardiyalara giriş-çıkış kayıtlarını görüntüleyebilirsiniz. Hangi personelin hangi vardiyada çalıştığı, giriş-çıkış saatleri ve durum bilgisi (normal, geç geldi, erken çıktı, vb.) görüntülenebilir. Personel adı, vardiya veya durum tipine göre filtreleme yapabilirsiniz. Son 30 günlük kayıtlar otomatik olarak görüntülenir.',
                  'This tab shows personnel check-in/check-out records, including shifts, hours, and status details. You can filter by personnel, shift, or status, and the last 30 days are displayed automatically.'
                )}
              />
            </ListItem>
            
            <Divider variant="inset" component="li" />
            
            <ListItem>
              <ListItemIcon>
                <TimerIcon sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary={tr('Toplam Süre', 'Total Duration')}
                secondary={tr(
                  'Bu sekmede personelin belirli bir tarih aralığında toplam çalışma sürelerini görüntüleyebilirsiniz. Personel bazlı veya tarih aralığı bazlı filtreler uygulayarak özel raporlar oluşturabilirsiniz. Toplam çalışma süresi, fazla mesai süreleri ve diğer istatistikler bu sekmede detaylı olarak görüntülenebilir.',
                  'This tab lets you view personnel total working durations within a selected date range. You can create custom reports with personnel and date filters, including overtime and other details.'
                )}
              />
            </ListItem>
          </List>
          
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            {tr('Ek Özellikler:', 'Additional Features:')}
          </Typography>
          
          <Typography paragraph>
            <strong>{tr('Excel İndir', 'Download Excel')}:</strong> {tr('Aktif sekmedeki verileri Excel dosyası olarak indirmenizi sağlar. İndirilen dosya, görüntülenen verileri ve filtreleme ayarlarını içerir.', 'Downloads the data in the active tab as an Excel file, including visible records and applied filters.')}
          </Typography>
          
          <Typography paragraph>
            <strong>{isManager ? tr('Şube QR', 'Branch QR') : tr('QR İndir', 'Download QR')}:</strong> {tr(`${isManager ? 'Şubenize' : 'Şirketinize'} özel QR kodu oluşturur ve indirir. Bu QR kodu, personel giriş-çıkışlarında kullanılan mobil uygulamaya bağlanmak için kullanılabilir.`, `Creates and downloads a QR code for ${isManager ? 'your branch' : 'your company'}, which can be used for personnel check-in and check-out in the mobile app.`)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInfoModal} autoFocus>
            {tr('Kapat', 'Close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Tasarım Modal'ı */}
      <VardiyaQrPrintModal 
        open={qrDesignModalOpen} 
        onClose={() => setQrDesignModalOpen(false)}
        companyId={userDetails?.companyId}
        branchId={branchId}
      />

      {/* QR İndir Modal'ı (şube seçimi + Giriş/Çıkış/Tek QR) */}
      <VardiyaQrIndirModal
        open={qrDownloadModalOpen}
        onClose={() => setQrDownloadModalOpen(false)}
        companyId={userDetails?.companyId}
        branchId={branchId}
        isManager={isManager}
      />
    </Paper>
  );
};

export default Shifts; 